import type { DeviceRecord } from '../data/types';

/**
 * Builds a flat export row from a DeviceRecord with Location, IsRetired,
 * RetiredDate, and RetireComment columns in the correct order:
 * DeviceName, Location, OS, Platforms..., Coverage, IsRetired, RetiredDate, RetireComment
 */
export function buildDeviceExportRow(device: DeviceRecord): Record<string, unknown> {
  const platformEntries = Object.entries(device.platforms);
  const totalPlatforms = platformEntries.length;
  const presentCount = platformEntries.filter(([, v]) => v).length;
  const coveragePct = totalPlatforms > 0 ? Math.round((presentCount / totalPlatforms) * 100) : 0;

  const row: Record<string, unknown> = {
    DeviceName: device.deviceName,
    Location: device.locationName ?? '',
    OS: device.osType,
  };

  // Add platform presence columns
  for (const [platform, present] of platformEntries) {
    row[platform] = present ? 'Yes' : 'No';
  }

  row['CoveragePct'] = coveragePct;
  row['Status'] = device.status;
  row['LastSeen'] = device.lastSeen;
  row['IsRetired'] = device.isRetired ? 'Yes' : 'No';
  row['RetiredDate'] = device.retiredDate ?? '';
  row['RetireComment'] = device.retireComment ?? '';

  return row;
}

/**
 * Builds export rows from DeviceRecord[], respecting the includeRetired toggle.
 * When includeRetired is false, retired devices are filtered out.
 */
export function buildDeviceExportRows(
  devices: DeviceRecord[],
  includeRetired: boolean
): Record<string, unknown>[] {
  const filtered = includeRetired ? devices : devices.filter((d) => !d.isRetired);
  return filtered.map(buildDeviceExportRow);
}

/**
 * useExport — client-side blob download hook for CSV and JSON exports.
 * Uses URL.createObjectURL + anchor click pattern for browser downloads.
 * Timestamps are embedded in filenames for easy identification.
 */
export function useExport() {
  /**
   * Formats current date/time as YYYY-MM-DD-HH-mm-ss for use in filenames.
   * Replaces colons and the T separator with hyphens.
   */
  function getTimestamp(): string {
    return new Date().toISOString().slice(0, 19).replace('T', '-').replace(/:/g, '-');
  }

  /**
   * Triggers a browser download of the given data serialized to JSON.
   * @param data Any JSON-serializable value
   * @param filenameBase Base filename without extension (timestamp + .json appended)
   */
  function downloadJson(data: unknown, filenameBase: string): void {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    triggerDownload(blob, `${filenameBase}-${getTimestamp()}.json`);
  }

  /**
   * Triggers a browser download of the given rows serialized to CSV.
   * Uses object keys from the first row as headers.
   * Adds UTF-8 BOM (\uFEFF) for Excel compatibility.
   * @param rows Array of flat objects; all objects should share the same keys
   * @param filenameBase Base filename without extension (timestamp + .csv appended)
   */
  function downloadCsv(rows: Record<string, unknown>[], filenameBase: string): void {
    if (rows.length === 0) {
      const blob = new Blob(['\uFEFF'], { type: 'text/csv;charset=utf-8;' });
      triggerDownload(blob, `${filenameBase}-${getTimestamp()}.csv`);
      return;
    }

    const headers = Object.keys(rows[0]);
    const headerRow = headers.map(escapeCsvCell).join(',');
    const dataRows = rows.map((row) =>
      headers.map((h) => escapeCsvCell(String(row[h] ?? ''))).join(',')
    );

    const csv = '\uFEFF' + [headerRow, ...dataRows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    triggerDownload(blob, `${filenameBase}-${getTimestamp()}.csv`);
  }

  /**
   * Creates a temporary anchor element to trigger a file download,
   * then immediately revokes the object URL to free memory.
   */
  function triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  /**
   * Escapes a CSV cell value: wraps in quotes if it contains commas, quotes, or newlines.
   * Doubles any existing double-quote characters per RFC 4180.
   */
  function escapeCsvCell(value: string): string {
    if (/[",\r\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }

  return { downloadJson, downloadCsv };
}
